import React from "react";
import { API } from "../../BlakeData";

import { EditionGroupView } from "./EditionGroupView";
import { EditionGroupRecord, ActiveIndex, emptyActiveIndex } from "../Types";
import { EditionRecord, BookRecord } from "../../BlakeData/Types";
import { Button } from "../Button";

import "./style/main.scss";
import { Dimensions } from "../Dimensions";
import Measure, { ContentRect } from 'react-measure';
import LoadingPage from "../LoadingPage";


/**
 * The index page of the application. Renders all edition groups 
 * and provides basic sorting functionality
 */
export default class HomePage extends React.Component<{}, {}>{

    state: {
        groups: Array<EditionGroupRecord>;
        groupingBy?: string;
        activeIndex: ActiveIndex;
        editions?: Array<EditionRecord>
        dimensions: Dimensions;
    }

    constructor(props: object) {
        super(props);
        this.state = {
            groups:[],
            activeIndex: emptyActiveIndex,
            dimensions: { width: 0, height: 0 }
        };



        this.setDimensions = this.setDimensions.bind(this);
        this.setGroups = this.setGroups.bind(this);
        this.setActiveIndex = this.setActiveIndex.bind(this);
        this.clearActiveIndex = this.clearActiveIndex.bind(this);
        this.getAllEditions = this.getAllEditions.bind(this);
        this.groupByYear = this.groupByYear.bind(this);
        this.groupByBook = this.groupByBook.bind(this);
        this.renderButtonTray = this.renderButtonTray.bind(this);
        this.renderEditionGroups = this.renderEditionGroups.bind(this);


        // the default grouping will be  by year
        this.groupByYear();
    }

    setDimensions(dimensions: { width: number, height: number }) {
        this.setState({ dimensions })
    }

    /**
     * Set the grouping (either by year or by book in this implementation)
     * @param label a label for the grouping
     * @param groupPromises an array of edition group promises
     */
    async setGroups(label: string, groupPromises: Array<Promise<EditionGroupRecord>>) {
        if (label === this.state.groupingBy) { return; }

        // TODO - This appears to be needed in order to force the loading page
        // and therefore unmount all of the child elements, forcing them
        // to be rerendered. A more complete fix would involve finding where the
        // edition ids are getting de-coupled from the prints. What's happening
        // is that without this line, when you switch modes, the wrong prints
        // become associated with an edition -- basically the prints don't change
        // but the editions (labels) do.
        this.setState({ groups: [] });
        
        this.clearActiveIndex();

        Promise.all(groupPromises).then(groups => {
            this.setState({ groupingBy: label, groups })
            console.log("new groups", groups)
        })

    }

    setActiveIndex(activeIndex: ActiveIndex) {
        this.setState({ activeIndex });
    }

    clearActiveIndex() {
        this.setState({
            activeIndex: emptyActiveIndex
        })
    }

    async getAllEditions(){

        if (!this.state.editions){
            // console.log("Making API call to get editions")
            const editions = await API.getAllEditions();
            this.setState({editions});
            return editions;
        } else {
            // console.log("No need to call getAllEditions")
        }
        return this.state.editions;

    }

    /**
     * Load and sort the edition data by year
     */
    async groupByYear() {
        // get all years
        let addedYears: number[] = [];
        // console.log("Getting all the editions!")
        // API.getAllEditions()
        this.getAllEditions()
            .then((editions: Array<EditionRecord>) => {
                // console.log("Got all the editions!")
                let years = editions.map((ed: EditionRecord) => {
                    return ed.year;
                }).filter((y: number) => {
                    if (addedYears.indexOf(y) >= 0) { return false; }
                    addedYears.push(y);
                    return true;
                }).sort()

                // for each year, get all editions
                const yearGroups = years.map(async (y: number) => {

                    // let editions = await dx.editions.where("year").equals(y).toArray();
                    let editions = await API.getEditionsByYear(y);

                    return {
                        title: `${y}`,
                        editions
                    };

                });

                this.setGroups("Year", yearGroups)
            });
    }

    /**
     * Load and sort the edition data by book
     */
    async groupByBook() {
        // get all the books and store them in the group state value
        const books = await API.getAllBooks();

        // now get all the editions
        const bookGroups = books.map(async (book: BookRecord) => {

            let editions = await API.getEditionsByBookID(book.id);

            return {
                title: book.title,
                editions: editions
            }
        });

        this.setGroups("Book", bookGroups);

    }

    /**
     * Sort the groups prior to rendering
     */
    static sortGroupsByTitle(groups: Array<EditionGroupRecord>) {
        return groups.sort((a: EditionGroupRecord, b: EditionGroupRecord) => {
            if (a.title < b.title) { return -1 }
            return 1;
        });
    }

    /**
     * Render the top button tray with sorter buttons
     */
    renderButtonTray() {
        return (
            <div className="button-tray" >
                <div className="label">Sort by</div>
                <Button text="year"
                    className={this.state.groupingBy === "Year" ? "current" : ""}
                    onClick={() => this.groupByYear()}></Button>
                <Button text="book"
                    className={this.state.groupingBy === "Book" ? "current" : ""}
                    onClick={() => this.groupByBook()}></Button>
            </div >
        );
    }

    /**
     * Render each edition group
     */
    renderEditionGroups(groups: Array<EditionGroupRecord>) {
        return groups.map((group: EditionGroupRecord, i: number) => {

            return <EditionGroupView
                groupIndex={i}
                activeIndex={this.state.activeIndex.groupIndex === i ? this.state.activeIndex : emptyActiveIndex}
                setActiveIndex={this.setActiveIndex}
                key={i}
                dimensions={this.state.dimensions}
                group={group} />;
        })
    }

    render() {

        // If there are no groups to render, just show a loading message
        if (!this.state.groups) {
            // return (<div>Loading...</div>)
            return <LoadingPage />
        }

        // Sort the groups by title
        const groups = HomePage.sortGroupsByTitle(this.state.groups);
        // console.log("Rendering HomePage");

        return (
            <Measure
                bounds
                onResize={(contentRect: ContentRect) => {
                    let { width, height } = contentRect.bounds || { width: 0, height: 0 };
                    this.setDimensions({
                        width,
                        height
                    });

                }}
            >
                {({ measureRef }) => (
                    <main ref={measureRef} className="HomePage">
                        {this.renderButtonTray()}
                        <div>
                            {this.renderEditionGroups(groups)}
                        </div>
                        <div className="bottom-carousel">
                            {/* {this.renderBottomCarousel()} */}
                        </div>
                    </main>
                )}


            </Measure>
        );
    }
}